/**
 * DiscussModal — Phase 6: "Discuss this article" modal
 * Shown when user clicks "Discuss" on an article card.
 *
 * Options:
 *   a) View existing discussions for this article
 *   b) Start a new discussion tied to this article
 *
 * PHI-Zero: article PMID and title are data, not user-entered text — safe to use.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { discussionsAPI } from '../api';
import { MessageSquare, Plus, ExternalLink, X, Loader2 } from 'lucide-react';

interface DiscussModalProps {
  pmid: string;
  articleTitle: string;
  specialtyId?: string;   // preferred specialty for pre-selection
  onClose: () => void;
}

const DiscussModal: React.FC<DiscussModalProps> = ({ pmid, articleTitle, specialtyId, onClose }) => {
  const navigate = useNavigate();
  const [threads, setThreads] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadExisting = async () => {
      if (!specialtyId) {
        setLoading(false);
        return;
      }
      try {
        // Fetch threads for this specialty filtered by article PMID
        const data = await discussionsAPI.getSpecialtyThreads(specialtyId, pmid);
        setThreads(data.threads || []);
      } catch {
        setThreads([]);
      } finally {
        setLoading(false);
      }
    };
    loadExisting();
  }, [pmid, specialtyId]);

  const handleStartNew = () => {
    // Navigate to new thread composer with article pre-attached
    const params = new URLSearchParams();
    if (specialtyId) params.set('specialty', specialtyId);
    params.set('pmid', pmid);
    params.set('articleTitle', articleTitle.slice(0, 120));
    navigate(`/community/new?${params.toString()}`);
    onClose();
  };

  const handleViewThread = (threadId: string) => {
    navigate(`/community/thread/${threadId}`);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="discuss-modal"
    >
      <Card className="w-full max-w-lg bg-white shadow-xl">
        <div className="flex items-start justify-between p-5 border-b">
          <div className="flex-1 pr-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-1">
              <MessageSquare className="h-4 w-4 text-blue-600 shrink-0" />
              Discuss this article
            </h3>
            <p className="responsive-title text-sm text-slate-500 line-clamp-2">{articleTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 focus:outline-none"
            aria-label="Close discuss modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Start new discussion */}
          <Button
            className="w-full bg-blue-600 hover:bg-blue-700 text-white justify-start gap-2"
            onClick={handleStartNew}
            data-testid="discuss-start-new-btn"
          >
            <Plus className="h-4 w-4" />
            Start a new discussion about this article
          </Button>

          {/* Existing discussions */}
          {loading ? (
            <div className="flex items-center justify-center py-4 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              <span className="text-sm">Loading existing discussions…</span>
            </div>
          ) : threads.length > 0 ? (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                Existing discussions ({threads.length})
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {threads.map(thread => (
                  <button
                    key={thread.thread_id}
                    onClick={() => handleViewThread(thread.thread_id)}
                    className="w-full text-left p-3 rounded-lg border hover:border-blue-200 hover:bg-blue-50 transition-colors"
                    data-testid={`discuss-existing-thread-${thread.thread_id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="responsive-title text-sm font-medium text-slate-900 truncate">{thread.title}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <MessageSquare className="h-3 w-3" />{thread.comment_count} comments
                          </span>
                          {thread.creator_is_verified && (
                            <Badge variant="secondary" className="text-[9px] bg-emerald-100 text-emerald-700 border-0">Verified</Badge>
                          )}
                        </div>
                      </div>
                      <ExternalLink className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : specialtyId ? (
            <p className="text-sm text-slate-400 text-center py-2">
              No existing discussions for this article. Be the first!
            </p>
          ) : (
            <p className="text-sm text-slate-400 text-center py-2">
              Select a specialty to view existing discussions.
            </p>
          )}
        </div>
      </Card>
    </div>
  );
};

export default DiscussModal;
